import { buildPublicUrl } from '@shared/config/env';
import type { InvoiceDto } from '@shared/api/contracts';
import type {
  InvoicePayCurrencyOption,
  InvoicePayConfirmations,
  PublicInvoicePay,
} from '../model/types';

const DEFAULT_SUPPORT_URL = 'mailto:support@paygatex.io';
const DEFAULT_RETURN_URL = buildPublicUrl('/');

const NETWORK_LABELS: Record<string, string> = {
  TRC20: 'TRON (TRX 20)',
  ERC20: 'Ethereum (ERC 20)',
  BEP20: 'BNB Smart Chain (BEP 20)',
  BTC: 'Bitcoin',
  LTC: 'Litecoin',
};

const toAmountString = (value: number | string | null | undefined): string => {
  if (value == null || value === '') return '0';
  const num = Number(value);
  if (Number.isFinite(num)) {
    return String(value);
  }
  return String(value);
};

const formatPayAmount = (value: number | string | null | undefined, currencyCode: string): string => {
  const raw = toAmountString(value);
  const code = currencyCode.toUpperCase();
  if (code === 'USDT' || code === 'USDC' || code === 'USD') {
    const num = Number(raw);
    return Number.isFinite(num) ? num.toFixed(4) : raw;
  }
  return raw;
};

const resolveAmountUsd = (dto: InvoiceDto, amount: string): string | null => {
  const code = (dto.currency?.short_code ?? dto.currency?.code ?? '').toUpperCase();
  if (code === 'USDT' || code === 'USDC') {
    return formatPayAmount(amount, 'USD');
  }
  const rate = dto.rate != null ? Number(dto.rate) : NaN;
  const amt = Number(amount);
  if (Number.isFinite(rate) && Number.isFinite(amt) && rate > 0) {
    return (amt * rate).toFixed(4);
  }
  return null;
};

const resolveNetworkCode = (dto: InvoiceDto): string => {
  const fromCurrency = (dto.currency as { network?: { short_code?: string; code?: string } })
    ?.network?.short_code
    ?? (dto.currency as { network?: { code?: string } })?.network?.code;
  if (fromCurrency) return String(fromCurrency);

  const fromTx = dto.transactions?.[0]?.network;
  if (fromTx) {
    return String(fromTx).replace(/-/g, '').toUpperCase().includes('TRC')
      ? 'TRC20'
      : String(fromTx);
  }

  return dto.currency?.short_code === 'BTC' ? 'BTC' : 'TRC20';
};

const resolveNetworkLabel = (code: string, dto: InvoiceDto): string => {
  if (NETWORK_LABELS[code]) return NETWORK_LABELS[code];
  const fromTx = dto.transactions?.[0]?.network;
  if (fromTx) return String(fromTx);
  return code;
};

const resolveConfirmations = (dto: InvoiceDto): InvoicePayConfirmations => {
  const topLevel = dto.confirmations;
  if (topLevel && (topLevel.required > 0 || topLevel.current > 0)) {
    return {
      current: topLevel.current,
      required: topLevel.required,
    };
  }

  const meta = dto.meta ?? {};
  const metaCurrent = Number(
    (meta.confirmationsCurrent as number | string | undefined)
      ?? (meta.confirmations as { current?: number })?.current
      ?? NaN
  );
  const metaRequired = Number(
    (meta.confirmationsRequired as number | string | undefined)
      ?? (meta.confirmations as { required?: number })?.required
      ?? NaN
  );

  return {
    current: Number.isFinite(metaCurrent) ? metaCurrent : 0,
    required: Number.isFinite(metaRequired) && metaRequired > 0 ? metaRequired : 0,
  };
};

const resolveRemaining = (dto: InvoiceDto, amount: string, paidAmount: string): string | null => {
  if (dto.status !== 'underpaid') return null;
  const total = Number(amount);
  const paid = Number(paidAmount);
  if (!Number.isFinite(total) || !Number.isFinite(paid)) return null;
  const remaining = Math.max(total - paid, 0);
  return String(remaining);
};

const buildAvailableCurrencies = (dto: InvoiceDto, network: string, networkLabel: string): InvoicePayCurrencyOption[] => {
  const code = dto.currency?.short_code ?? dto.currency?.code ?? 'USDT';
  return [
    {
      code,
      iconUrl: dto.currency?.icon_base,
      networks: [{ code: network, label: networkLabel }],
    },
  ];
};

const resolveReturnUrl = (dto: InvoiceDto): string => {
  const meta = dto.meta ?? {};
  const raw =
    (meta.returnUrl as string | undefined)
    ?? (meta.return_url as string | undefined)
    ?? (meta.link_success as string | undefined)
    ?? null;
  if (raw && typeof raw === 'string' && raw.trim()) return raw.trim();
  return DEFAULT_RETURN_URL;
};

const resolveSupportUrl = (dto: InvoiceDto): string => {
  const meta = dto.meta ?? {};
  const raw =
    (meta.supportUrl as string | undefined)
    ?? (meta.support_url as string | undefined)
    ?? null;
  if (raw && typeof raw === 'string' && raw.trim()) return raw.trim();
  return DEFAULT_SUPPORT_URL;
};

export const mapInvoiceDtoToPublicPay = (dto: InvoiceDto): PublicInvoicePay => {
  const currency = dto.currency?.short_code ?? dto.currency?.code ?? '';
  const amount = formatPayAmount(dto.totalAmount ?? dto.amount, currency);
  const paidAmount = formatPayAmount(dto.paidAmount ?? 0, currency);
  const network = resolveNetworkCode(dto);
  const networkLabel = resolveNetworkLabel(network, dto);
  const address = dto.address?.address?.trim() ? dto.address.address : null;
  const qrRaw = dto.address?.qr?.trim() ? dto.address.qr : null;

  return {
    id: String(dto.id),
    status: dto.status,
    amount,
    currency,
    network,
    networkLabel,
    amountUsd: resolveAmountUsd(dto, amount),
    address,
    qrUrl: qrRaw,
    expiresAt: dto.expiresAt ?? null,
    paidAmount,
    remainingAmount: resolveRemaining(dto, amount, paidAmount),
    confirmations: resolveConfirmations(dto),
    txid: dto.transactions?.[0]?.txid ?? null,
    availableCurrencies: buildAvailableCurrencies(dto, network, networkLabel),
    returnUrl: resolveReturnUrl(dto),
    supportUrl: resolveSupportUrl(dto),
    serverTime: null,
    currencyIconUrl: dto.currency?.icon_base,
    networkIconUrl: dto.currency?.icon_network,
  };
};
